import Amex from "chameleon/icons/amex";
import ApplePayMark from "chameleon/icons/apple-pay-mark";
import CVV from "chameleon/icons/cvv";
import Diners from "chameleon/icons/diner";
import Discover from "chameleon/icons/discover";
import Jcb from "chameleon/icons/jcb";
import Maestro from "chameleon/icons/maestro";
import MasterCard from "chameleon/icons/mastercard";
import UnionPay from "chameleon/icons/union-pay";
import UnknownCard from "chameleon/icons/unknown-card";
import Visa from "chameleon/icons/visa";
import { Text } from "chameleon/lib/components/text/text";
import { Tooltip } from "chameleon/lib/components/tooltip/tooltip";

import {
  displayCardsPaddingClass,
  displayCardsSpanClass,
  displayCardsWrapperClass,
  displayPaymentNameIconClass,
} from "./display-cards.css";

export type DisplayCardsProps = {
  /**
   * What payment types do you support?
   */
  supportedPaymentTypes?: Array<CreditCardNetwork>;
  isPaymentNameWithIcon?: boolean;
  translatedValues?: {
    noCard: string;
    americanExpress: string;
    cvv: string;
    discover: string;
    jcb: string;
    visa: string;
    mastercard: string;
    maestro: string;
    applePay: string;
    unionPay: string;
    dinersClub: string;
  };
  /**
   * Maximum number of cards to be displayed.
   */
  maxCardsDisplayed?: number;
  /**
   * Maximum width of cards displayed.  Setting this will take precedence over maxCardsDisplayed.
   */
  maxWidth?: number;
  /**
   * How content should be justified.  Defaults to "normal".
   */
  justifyContent?: string;
};

export type CreditCardNetwork =
  | "amex"
  | "visa"
  | "mastercard"
  | "maestro"
  | "discover"
  | "cvv"
  | "jcb"
  | "unionPay"
  | "dinersClub";

export type WalletPaymentTypes = "applePay";

/**
 * Component used to display accepted payment types in icons
 */
export const DisplayCards = ({
  supportedPaymentTypes = [],
  isPaymentNameWithIcon,
  translatedValues,
  maxCardsDisplayed,
  maxWidth,
  justifyContent = "normal",
}: DisplayCardsProps) => {
  type Asset = {
    alt: string;
    Icon: () => JSX.Element;
  };

  let calculatedCardsDisplayed: number | undefined = undefined;
  if (maxWidth) {
    if (supportedPaymentTypes.length % 2 === 0) {
      // even... calc how many can be displayed (min 1), remove extra 4px for gap, and remove 1 card to make room for +n text
      calculatedCardsDisplayed = Math.max(
        1,
        Math.floor((maxWidth - 4) / 40) - 1
      );
    } else {
      // odd.. calc how many can be displayed (min 1), and remove 1 card to make room for +n text
      calculatedCardsDisplayed = Math.max(1, Math.floor(maxWidth / 40) - 1);
    }
  } else {
    // catch-all for no maxWidth specified
    calculatedCardsDisplayed = maxCardsDisplayed; // can be undefined
  }

  const cardAssets: Record<CreditCardNetwork | WalletPaymentTypes, Asset> = {
    amex: {
      alt: translatedValues?.americanExpress ?? "American Express",
      Icon: () => <Amex sizeVariant={24} isRectangle />,
    },
    cvv: {
      alt: translatedValues?.cvv ?? "CVV",
      Icon: () => (
        <CVV
          sizeVariant={24}
          isRectangle
          role="presentation"
          aria-hidden={"true"}
        />
      ),
    },
    discover: {
      alt: translatedValues?.discover ?? "Discover",
      Icon: () => <Discover sizeVariant={24} isRectangle />,
    },
    jcb: {
      alt: translatedValues?.jcb ?? "JCB",
      Icon: () => <Jcb sizeVariant={24} isRectangle />,
    },
    visa: {
      alt: translatedValues?.visa ?? "Visa",
      Icon: () => <Visa sizeVariant={24} isRectangle />,
    },
    mastercard: {
      alt: translatedValues?.mastercard ?? "Mastercard",
      Icon: () => <MasterCard sizeVariant={24} isRectangle />,
    },
    maestro: {
      alt: translatedValues?.maestro ?? "Maestro",
      Icon: () => <Maestro sizeVariant={24} isRectangle />,
    },
    applePay: {
      alt: translatedValues?.applePay ?? "ApplePay",
      Icon: () => <ApplePayMark sizeVariant={24} isRectangle />,
    },
    unionPay: {
      alt: translatedValues?.unionPay ?? "UnionPay",
      Icon: () => <UnionPay sizeVariant={24} isRectangle />,
    },
    dinersClub: {
      alt: translatedValues?.dinersClub ?? "Diners Club",
      Icon: () => <Diners sizeVariant={24} isRectangle />,
    },
  };

  const getCardsContent = () => {
    let content = null;

    if (supportedPaymentTypes?.length === 0) {
      // return no-card content immediately if no cards.  The End.
      return (
        <span
          className={`${displayCardsSpanClass} ${displayCardsPaddingClass}`}
          key="no-card"
        >
          {<UnknownCard sizeVariant={24} isRectangle />}
        </span>
      );
    }

    if (calculatedCardsDisplayed) {
      // generate card content up to `maxCardsDisplayed` and append +n text.
      if (supportedPaymentTypes?.length <= calculatedCardsDisplayed) {
        content = supportedPaymentTypes.map((paymentType) => {
          const cardData = cardAssets[paymentType];
          if (cardData) {
            return (
              <span
                className={`${displayCardsSpanClass} ${supportedPaymentTypes?.length === 1 && displayCardsPaddingClass} ${isPaymentNameWithIcon && displayPaymentNameIconClass}`}
                key={cardData.alt}
                aria-label={cardData.alt}
                role={"img"}
              >
                {cardData.Icon()}
              </span>
            );
          }
          return <></>;
        });
      } else if (supportedPaymentTypes?.length > calculatedCardsDisplayed) {
        const tooltipContentText = supportedPaymentTypes
          .slice(calculatedCardsDisplayed, supportedPaymentTypes.length)
          .map((paymentType) => {
            const cardData = cardAssets[paymentType];
            if (cardData) {
              return (
                <div key={cardData.alt} style={{ whiteSpace: "nowrap" }}>
                  {cardData.alt}
                </div>
              );
            }
            return "";
          });
        const tooltipContentElement = <div>{tooltipContentText}</div>;
        content = supportedPaymentTypes
          .slice(0, calculatedCardsDisplayed)
          .map((paymentType) => {
            const cardData = cardAssets[paymentType];
            if (cardData) {
              return (
                <span
                  className={`${displayCardsSpanClass} ${supportedPaymentTypes?.length === 1 && displayCardsPaddingClass} ${isPaymentNameWithIcon && displayPaymentNameIconClass}`}
                  key={cardData.alt}
                  aria-label={cardData.alt}
                  role={"img"}
                >
                  {cardData.Icon()}
                </span>
              );
            }
            return <></>;
          })
          .concat(
            <Tooltip content={tooltipContentElement}>
              <Text styleVariant="caption">
                +{supportedPaymentTypes.length - calculatedCardsDisplayed}
              </Text>
            </Tooltip>
          );
      }
    } else {
      // no `calculatedCardsDisplayed`, generate all card content that are passed in
      content = supportedPaymentTypes.map((paymentType) => {
        const cardData = cardAssets[paymentType];
        if (cardData) {
          return (
            <span
              className={`${displayCardsSpanClass} ${supportedPaymentTypes?.length === 1 && displayCardsPaddingClass} ${isPaymentNameWithIcon && displayPaymentNameIconClass}`}
              key={cardData.alt}
              aria-label={cardData.alt}
              title={cardData.alt}
              role="img"
            >
              {cardData.Icon()}
            </span>
          );
        }
        return <></>;
      });
    }
    return content;
  };

  return (
    <div
      data-testid="display-cards"
      style={{ justifyContent: justifyContent }}
      className={displayCardsWrapperClass}
    >
      {getCardsContent()}
    </div>
  );
};
